/* eslint-disable camelcase */
import type { MessageType } from './hooks/types';

export const FeedbackType = {
  THUMBS_UP: 'thumbs_up',
  THUMBS_DOWN: 'thumbs_down',
} as const;

export type FeedbackTypeValue = typeof FeedbackType[keyof typeof FeedbackType];

export const FeedbackCategories = {
  EMOJI_USAGE: 'emoji_usage',
  OFFER_DETAILS: 'offer_details',
  BRAND_VOICE_TONE: 'brand_voice_tone',
  PRODUCT_REFERENCE: 'product_reference',
  URGENCY: 'urgency',
  INCORRECT_OR_INCOMPLETE: 'incorrect_or_incomplete',
  DIDNT_ANSWER_QUESTION: 'didnt_answer_question',
  HARD_TO_UNDERSTAND: 'hard_to_understand',
  SLOW_OR_BUGGY: 'slow_or_buggy',
  DATA_DOESNT_LOOK_RIGHT: 'data_doesnt_look_right',
  OTHER: 'other',
} as const;

export type FeedbackCategory =
  typeof FeedbackCategories[keyof typeof FeedbackCategories];

export const DEFAULT_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  [FeedbackCategories.EMOJI_USAGE]: 'Emoji Usage',
  [FeedbackCategories.OFFER_DETAILS]: 'Offer Details',
  [FeedbackCategories.BRAND_VOICE_TONE]: 'Brand Voice & Tone',
  [FeedbackCategories.PRODUCT_REFERENCE]: 'Product Reference',
  [FeedbackCategories.URGENCY]: 'Urgency',
  [FeedbackCategories.INCORRECT_OR_INCOMPLETE]: 'Incorrect or incomplete',
  [FeedbackCategories.DIDNT_ANSWER_QUESTION]: "Didn't answer my question",
  [FeedbackCategories.HARD_TO_UNDERSTAND]: 'Hard to understand',
  [FeedbackCategories.SLOW_OR_BUGGY]: 'Slow or buggy',
  [FeedbackCategories.DATA_DOESNT_LOOK_RIGHT]: "Data doesn't look right",
  [FeedbackCategories.OTHER]: 'Other',
};

export interface CategoryBannerConfig {
  message: string;
  buttonLabel: string;
  route: string;
}

// "Other" intentionally omitted — no banner suggestion fits the catch-all
// category, so the panel renders without a banner after submission.
export const CATEGORY_BANNER_CONFIG: Partial<
  Record<FeedbackCategory, CategoryBannerConfig>
> = {
  [FeedbackCategories.EMOJI_USAGE]: {
    message:
      "Adjust your Brand Center Preferred Content to control which emojis are used and how they reflect your brand's voice.",
    buttonLabel: 'Go to Preferred Content',
    route: '/brand/brand-guidelines#preferred-content',
  },
  [FeedbackCategories.PRODUCT_REFERENCE]: {
    message:
      'Ensure your product catalog is accurate and up to date so the correct items, pricing, and availability are referenced in messages.',
    buttonLabel: 'Go to Product Catalog',
    route: '/brand/product-catalog',
  },
  [FeedbackCategories.BRAND_VOICE_TONE]: {
    message:
      'Update your Brand Center voice and tone settings to adjust how messages are generated in future conversations.',
    buttonLabel: 'Go to Brand Center',
    route: '/brand/brand-guidelines#tone',
  },
  [FeedbackCategories.OFFER_DETAILS]: {
    message:
      'Review your active offers to ensure discounts, exclusions, and promotion details are reflected accurately in messages.',
    buttonLabel: 'Go to Offers',
    route: '/brand/shopper/settings#strategic-offers',
  },
  [FeedbackCategories.URGENCY]: {
    message:
      'Adjust your Brand Center urgency settings to control how time-sensitive language is used in messages.',
    buttonLabel: 'Go to Brand Center',
    route: '/brand/brand-guidelines#urgency',
  },
};

export interface FeedbackMetadata {
  selectedPills?: string[];
}

export interface BaseFeedbackPayload {
  message_id: string;
  product_area: string;
  feedback_type: FeedbackTypeValue;
  category?: string;
  feedback_text?: string;
}

const SHOPPER_FULL: FeedbackCategory[] = [
  FeedbackCategories.EMOJI_USAGE,
  FeedbackCategories.OFFER_DETAILS,
  FeedbackCategories.BRAND_VOICE_TONE,
  FeedbackCategories.PRODUCT_REFERENCE,
  FeedbackCategories.URGENCY,
  FeedbackCategories.OTHER,
];

const IT_NO_OFFER: FeedbackCategory[] = [
  FeedbackCategories.EMOJI_USAGE,
  FeedbackCategories.BRAND_VOICE_TONE,
  FeedbackCategories.PRODUCT_REFERENCE,
  FeedbackCategories.URGENCY,
  FeedbackCategories.OTHER,
];

const ASK_SHOPPER_REPORT: FeedbackCategory[] = [
  FeedbackCategories.INCORRECT_OR_INCOMPLETE,
  FeedbackCategories.DIDNT_ANSWER_QUESTION,
  FeedbackCategories.HARD_TO_UNDERSTAND,
  FeedbackCategories.SLOW_OR_BUGGY,
  FeedbackCategories.DATA_DOESNT_LOOK_RIGHT,
  FeedbackCategories.OTHER,
];

export const REASON_SETS_BY_MESSAGE_TYPE: Record<
  MessageType,
  FeedbackCategory[]
> = {
  preview: SHOPPER_FULL,
  shopper_response: SHOPPER_FULL,
  shopper_automation: SHOPPER_FULL,
  playground: SHOPPER_FULL,
  infinity_testing: IT_NO_OFFER,
  infinity_testing_campaign: IT_NO_OFFER,
  infinity_testing_automation: IT_NO_OFFER,
  brand_center_it_preview: IT_NO_OFFER,
  outreach_message: SHOPPER_FULL,
  ask_shopper_report: ASK_SHOPPER_REPORT,
};
